import React, { useState, forwardRef } from 'react';
import MuiTextField, { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField';
import InputMask from '@mona-health/react-input-mask';

export interface TextFieldProps extends Omit<MuiTextFieldProps, 'inputMask' | 'maskChar' | 'formatChars'> {
  inputMask?: string;
  maskChar?: string | null;
  formatChars?: Record<string, string>;
}

const MASK_PROPS = ['maskChar', 'formatChars', 'alwaysShowMask', 'maskPlaceholder', 'beforeMaskedStateChange'];

const MaskedTextField = forwardRef<HTMLDivElement, any>(function MaskedTextField(props, ref) {
  const cleaned = { ...props };
  for (const key of MASK_PROPS) delete cleaned[key];
  return <MuiTextField ref={ref} {...cleaned} />;
});

const TextField: React.FC<TextFieldProps> = ({
  inputMask,
  maskChar = null,
  formatChars,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);

  const shouldShrink = props.InputLabelProps?.shrink !== undefined
    ? props.InputLabelProps.shrink
    : (Boolean(props.value) || isFocused);

  const inputLabelProps = {
    ...props.InputLabelProps,
    shrink: shouldShrink,
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (props.onFocus) props.onFocus(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (props.onBlur) props.onBlur(event);
  };

  if (!inputMask) {
    return (
      <MuiTextField
        {...props}
        InputLabelProps={inputLabelProps}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    );
  }

  const maskValue = props.value != null ? String(props.value) : '';
  return (
    <InputMask
      mask={inputMask}
      value={maskValue}
      onChange={props.onChange}
      maskChar={maskChar}
      {...(formatChars ? { formatChars } : {})}
      disabled={props.disabled}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <MaskedTextField
        {...props}
        value={undefined}
        onChange={undefined}
        onFocus={undefined}
        onBlur={undefined}
        disabled={undefined}
        InputLabelProps={inputLabelProps}
      />
    </InputMask>
  );
};

export default TextField;
